import { useState, useEffect, useCallback, useRef } from 'react';
import { listTenants, type ListTenantsParams } from '../api/superAdminApi';
import type { TenantRow } from '../types';

interface UseOrganisationListResult {
  tenants: TenantRow[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
  search: string;
  statusFilter: string;
  setSearch: (s: string) => void;
  setStatusFilter: (s: string) => void;
  setPage: (p: number) => void;
  refresh: () => void;
}

const LIMIT = 50;
const DEBOUNCE_MS = 300;

export function useOrganisationList(): UseOrganisationListResult {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearchRaw] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const doFetch = useCallback(async (params: ListTenantsParams) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const thisRequestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await listTenants(params, controller.signal);
      if (thisRequestId !== requestIdRef.current) return;
      setTenants(data.tenants || []);
      setTotal(data.total || 0);
    } catch (err: unknown) {
      if (thisRequestId !== requestIdRef.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load organisations');
    } finally {
      if (thisRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const buildParams = useCallback(
    (): ListTenantsParams => ({
      search: search || undefined,
      status: statusFilter || undefined,
      page,
      limit: LIMIT,
    }),
    [search, statusFilter, page]
  );

  const setSearch = useCallback((s: string) => {
    setSearchRaw(s);
    setPage(1);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(buildParams()), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, statusFilter, page, doFetch, buildParams]);

  // Cleanup on unmount
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  return {
    tenants, total, page, loading, error,
    search, statusFilter,
    setSearch, setStatusFilter,
    setPage: (p) => setPage(p),
    refresh: () => doFetch(buildParams()),
  };
}
